import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "utils";
import { useSnapshot } from "valtio";
import { Icon } from "@ospweb/sdk-ui";
import { ChatStore } from "@store/modules/chat";
import type { ChatMode } from "@store/types";
import { AGENT_CHAT_MODE } from "@utils/chatMode";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Sender/ChatModeSelector";
import { buildOpenReplayLabel } from "@utils/openreplay";

const I18n = I18nUtil.createScoped(Translations);

function getModeIcon(mode: ChatMode): string {
  if (mode === "qa") {
    return "chat-smile";
  }
  return "best";
}

export function ChatModeSelector({
  disabled = false,
  canSelectMode,
  openReplaySurface: _openReplaySurface,
}: {
  disabled?: boolean;
  canSelectMode?: (mode: ChatMode) => boolean;
  /** Kept for API compatibility with Sender; replay uses v2 DOM labels only. */
  openReplaySurface?: string;
}) {
  void _openReplaySurface;
  const { chatMode } = useSnapshot(ChatStore);
  const currentMode = chatMode || "qa";
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const wrapperRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current?.contains(e.target as Node) ||
        popoverRef.current?.contains(e.target as Node)
      ) {
        return;
      }
      setOpen(false);
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open]);

  const handleSelectMode = useCallback(
    (mode: ChatMode) => {
      if (disabled) {
        return;
      }
      if (typeof canSelectMode === "function" && !canSelectMode(mode)) {
        setOpen(false);
        return;
      }
      ChatStore.chatMode = mode;
      setOpen(false);
    },
    [disabled, canSelectMode, currentMode],
  );

  const currentLabel =
    currentMode === AGENT_CHAT_MODE ? I18n.t("Think") : I18n.t("Fast");

  const OR_SCENE = "ChatBox";

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        data-openreplay-label={buildOpenReplayLabel(
          OR_SCENE,
          "ChatMode",
          "Toggle",
        )}
        disabled={disabled}
        className={cn(
          "button sm_button rounded-6 ring-1 ring-border_inverse_transparent20 text-content_primary md:not-loading-and-disabled:hover:bg-background_white_overlay5",
        )}
        onPointerDown={(event) => {
          event.preventDefault();
        }}
        onClick={() => {
          if (disabled) {
            return;
          }
          if (!open && wrapperRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();
            setDropdownStyle({
              position: "fixed",
              bottom: window.innerHeight - rect.top + 10,
              left: rect.left,
              zIndex: 9999,
            });
          }
          setOpen((prev) => !prev);
        }}
      >
        <span className="button_m text-content_primary">{currentLabel}</span>
        <Icon
          name="chevron-down"
          size={20}
          className={cn(
            "transition-transform duration-150",
            open ? "rotate-180" : "rotate-0",
          )}
        />
      </button>
      {open &&
        typeof window !== "undefined" &&
        createPortal(
          <div
            ref={popoverRef}
            style={dropdownStyle}
            className={cn(
              "min-w-[140px] rounded-2 p-2",
              "bg-background_solid_neutral_primary",
              "border border-solid border-border_solid",
              "shadow-[0_0_32px_0_rgba(0,0,0,0.08)]",
            )}
          >
            {/* Fast Mode */}
            <button
              aria-label="Chat Mode Selector"
              data-openreplay-label={buildOpenReplayLabel(
                OR_SCENE,
                "ChatMode",
                "SelectFast",
              )}
              id="btn-chat-mode-selector"
              type="button"
              className={cn(
                "flex items-center gap-2 w-full p-2 rounded-1.5",
                "text-left body_s cursor-pointer",
                "hover:bg-background_white_overlay5",
                "transition-colors duration-100",
                currentMode === "qa"
                  ? "text-content_brand_primary"
                  : "text-content_primary",
              )}
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => handleSelectMode("qa")}
            >
              <Icon name="chat-smile" size={16} />
              <span className="body_medium_m">{I18n.t("Fast")}</span>
              {currentMode === "qa" && (
                <Icon
                  name="check"
                  size={20}
                  className="ml-auto text-content_brand_primary"
                />
              )}
            </button>

            {/* <div className="mx-2 my-1 border-t border-solid border-border_white_transparent10" /> */}
            <button
              aria-label="Chat Mode Selector"
              data-openreplay-label={buildOpenReplayLabel(
                OR_SCENE,
                "ChatMode",
                "SelectAgent",
              )}
              id="btn-chat-mode-selector-2"
              type="button"
              className={cn(
                "flex items-center gap-2 w-full p-2 rounded-1.5",
                "text-left body_s cursor-pointer",
                "hover:bg-background_white_overlay5",
                "transition-colors duration-100",
                currentMode === AGENT_CHAT_MODE
                  ? "text-content_brand_primary"
                  : "text-content_primary",
              )}
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => handleSelectMode(AGENT_CHAT_MODE)}
            >
              <Icon name={getModeIcon(AGENT_CHAT_MODE) as any} size={16} />
              <span className="body_medium_m">{I18n.t("Think")}</span>
              {currentMode === AGENT_CHAT_MODE && (
                <Icon
                  name="check"
                  size={20}
                  className="ml-auto text-content_brand_primary"
                />
              )}
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
}
